import { z } from 'zod'

const boundedText = (maximum: number) => z.string().trim().max(maximum)
const phonePattern = /^\+?[0-9() .-]+$/

export const onboardingPhoneSchema = boundedText(50).refine((value) => {
  if (!value) return true
  const digits = [...value].filter((character) => /\d/.test(character)).length
  return phonePattern.test(value) && digits >= 7 && digits <= 15
}, 'Enter a valid phone number. A country code is not added automatically.')

export const onboardingClinicInfoDraftSchema = z
  .object({
    dentistryName: boundedText(120).optional(),
    dentistryAddress: boundedText(500).optional(),
    dentistryPhone: onboardingPhoneSchema.optional(),
    dentistryEmail: z
      .union([z.literal(''), z.string().trim().email('Enter a valid email.')])
      .refine((value) => value.length <= 254, 'Email is too long.')
      .optional(),
    emergencyPhone: onboardingPhoneSchema.optional(),
  })
  .strict()

const workingHoursIntervalSchema = z
  .string()
  .regex(
    /^(?:[01]\d|2[0-3]):[0-5]\d-(?:[01]\d|2[0-3]):[0-5]\d$/,
    'Use a valid 24-hour time range.'
  )
  .refine((value) => {
    if (value === '00:00-00:00') return true
    return value.slice(0, 5) < value.slice(6)
  }, 'Closing time must be later than opening time.')

export const onboardingWorkingHoursSchema = z
  .object({
    sun: workingHoursIntervalSchema,
    mon: workingHoursIntervalSchema,
    tue: workingHoursIntervalSchema,
    wed: workingHoursIntervalSchema,
    thu: workingHoursIntervalSchema,
    fri: workingHoursIntervalSchema,
    sat: workingHoursIntervalSchema,
  })
  .strict()

type SupportedValuesIntl = typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[]
}

let cachedTimeZones: string[] | undefined

export function getSupportedTimeZones(): string[] {
  if (cachedTimeZones) return cachedTimeZones
  const supported = (Intl as SupportedValuesIntl).supportedValuesOf?.(
    'timeZone'
  ) ?? [Intl.DateTimeFormat().resolvedOptions().timeZone]
  cachedTimeZones = [...new Set(['UTC', ...supported.filter(Boolean)])].sort()
  return cachedTimeZones
}

export const onboardingTimeZoneSchema = z
  .string()
  .trim()
  .refine(
    (value) => getSupportedTimeZones().includes(value),
    'Select a canonical IANA timezone.'
  )

export const profileDraftSchema = z
  .object({
    clinicInfo: onboardingClinicInfoDraftSchema.optional(),
    timeZone: onboardingTimeZoneSchema.optional(),
    workingHours: onboardingWorkingHoursSchema.optional(),
  })
  .strict()

export const profileResponseSchema = z
  .object({
    profileDraft: profileDraftSchema,
    revision: z.number().int().positive(),
    isComplete: z.boolean(),
  })
  .strict()

export const profileFormSchema = z
  .object({
    clinicInfo: z
      .object({
        dentistryName: boundedText(120),
        dentistryAddress: boundedText(500),
        dentistryPhone: onboardingPhoneSchema,
        dentistryEmail: z
          .union([
            z.literal(''),
            z.string().trim().email('Enter a valid email.'),
          ])
          .refine((value) => value.length <= 254, 'Email is too long.'),
        emergencyPhone: onboardingPhoneSchema,
      })
      .strict(),
    timeZone: onboardingTimeZoneSchema,
    workingHours: onboardingWorkingHoursSchema,
  })
  .strict()

export const completeProfileFormSchema = profileFormSchema.superRefine(
  (value, context) => {
    if (value.clinicInfo.dentistryName.length < 2) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clinicInfo', 'dentistryName'],
        message: 'Clinic name must contain at least 2 characters.',
      })
    }
    if (value.clinicInfo.dentistryAddress.length < 5) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clinicInfo', 'dentistryAddress'],
        message: 'Enter the clinic address.',
      })
    }
    if (!value.clinicInfo.dentistryPhone) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clinicInfo', 'dentistryPhone'],
        message: 'Clinic phone is required.',
      })
    }
    if (!value.clinicInfo.dentistryEmail) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clinicInfo', 'dentistryEmail'],
        message: 'Clinic email is required.',
      })
    }
  }
)

export type ProfileDraft = z.infer<typeof profileDraftSchema>
export type ProfileResponse = z.infer<typeof profileResponseSchema>
export type ProfileForm = z.infer<typeof profileFormSchema>
export type OnboardingWorkingHours = z.infer<
  typeof onboardingWorkingHoursSchema
>
